import { useContext } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { fetchRest, RestApiError, useRestMutation } from '@/lib/hook/tanstackQuery'
import { ApiMutationEndpoint } from '@/src/ApiMutationEndpoint'
import ProfileContext from '@/components/Context/ProfileContext'

const addToWishlistSchema = z.object({
  products: z.record(z.string(), z.object({
    product_id: z.string(),
    amount: z.number().default(1),
  })),
})

type AddToWishlistInput = z.infer<typeof addToWishlistSchema>

export const useAddToWishlist = () => {
  const queryClient = useQueryClient()

  return useRestMutation<unknown, Error, AddToWishlistInput>({
    ...ApiMutationEndpoint.USERS.ADD_TO_WISHLIST,
    schema: addToWishlistSchema,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] })
    },
  })
}

export const useRemoveFromWishlist = () => {
  const queryClient = useQueryClient()

  return useRestMutation<unknown, Error, { id: string }>({
    ...ApiMutationEndpoint.USERS.REMOVE_FROM_WISHLIST,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wishlist'] })
    },
  })
}

export const useMoveToWishlist = () => {
  const queryClient = useQueryClient()
  const { basketAllRefetcher } = useContext(ProfileContext)

  const moveToWishlist = async (product: {
    product_id: string
    item_id: string
  }) => {
    try {
      // 1. Remove from cart (REST DELETE cart_content/:item_id)
      await fetchRest({
        endpoint: ApiMutationEndpoint.BASKET.REMOVE_PRODUCT.endpoint,
        method: ApiMutationEndpoint.BASKET.REMOVE_PRODUCT.method,
        variables: { item_id: product.item_id },
      })

      // 2. Add to wishlist (REST) — 409 means already in wishlist, treat as success
      try {
        await fetchRest({
          endpoint: 'wish_list',
          method: 'POST',
          body: {
            products: {
              [product.product_id]: {
                product_id: product.product_id,
                amount: 1,
              },
            },
          },
        })
      } catch (e) {
        if (!(e instanceof RestApiError && e.status === 409)) throw e
      }

      // 3. Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['wishlist'] })
      await basketAllRefetcher()

      // @ts-ignore - global.t is set by CurrentLocale
      toast.success(t({ id: 'web_wishlist_product_saved' }))
    } catch (error) {
      // @ts-ignore
      toast.error(t({ id: 'web_wishlist_error' }))
      throw error
    }
  }

  return { moveToWishlist }
}
